export type ApiErrorDetails = Record<string, unknown> | null

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly traceId?: string,
    readonly details?: ApiErrorDetails,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type Envelope<T> = { data: T; trace_id?: string; code?: string; message?: string; details?: ApiErrorDetails }
export type ResponseWithMeta<T> = { data: T; traceId?: string }
export const ADMIN_AUTH_REQUIRED_EVENT = 'learning-platform:admin-auth-required'

let sessionRestoreRequest: Promise<void> | null = null
let sessionMutationTail: Promise<void> = Promise.resolve()
const adminSessionLock = 'welearn:admin:session-refresh'

function dispatchAuthRequired() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(ADMIN_AUTH_REQUIRED_EVENT))
}

async function adminApiFetch(base: string, path: string, init: RequestInit = {}) {
  return fetch(base + path, {
    ...init,
    credentials: 'include',
    headers: { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
  })
}

async function hasAuthenticatedAdminSession(base: string) {
  const response = await adminApiFetch(base, '/auth/admin/session', { cache: 'no-store' })
  if (!response.ok) throw new ApiError('管理端登录状态检查失败', response.status, 'SESSION_CHECK_FAILED')
  const body = await response.json().catch(() => null) as Envelope<{ authenticated?: boolean }> | null
  return body?.data.authenticated === true
}

async function restoreAdminSessionInsideLock(base: string) {
  if (await hasAuthenticatedAdminSession(base)) return
  const response = await adminApiFetch(base, '/auth/admin/refresh', { method: 'POST', body: '{}' })
  if (!response.ok) throw new ApiError(response.status === 401 ? '管理端登录已过期' : '管理端会话暂时无法恢复', response.status, 'SESSION_REFRESH_FAILED')
}

export async function runWithAdminSessionMutationLock<T>(task: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return await navigator.locks.request<Promise<T>>(adminSessionLock, { mode: 'exclusive' }, task)
  }
  const result = sessionMutationTail.then(task, task)
  sessionMutationTail = result.then(() => undefined, () => undefined)
  return await result
}

export function restoreAdminSession(base = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080/api/v1') {
  if (!sessionRestoreRequest) {
    sessionRestoreRequest = runWithAdminSessionMutationLock(() => restoreAdminSessionInsideLock(base))
      .finally(() => { sessionRestoreRequest = null })
  }
  return sessionRestoreRequest
}

export class HttpClient {
  constructor(private readonly base = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080/api/v1') {}

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    return (await this.requestWithMeta<T>(path, init)).data
  }

  async requestWithMeta<T>(path: string, init: RequestInit = {}, retrySession = true): Promise<ResponseWithMeta<T>> {
    const response = await adminApiFetch(this.base, path, init)
    const body = await response.json().catch(() => null) as Envelope<T> | null
    const traceId = response.headers.get('X-Request-ID') ?? body?.trace_id
    if (init.signal?.aborted) throw init.signal.reason ?? new DOMException('请求已取消', 'AbortError')
    if (response.status === 401 && retrySession && !path.startsWith('/auth/')) {
      try {
        await restoreAdminSession(this.base)
        return this.requestWithMeta<T>(path, init, false)
      } catch (error) {
        if (init.signal?.aborted) throw init.signal.reason ?? new DOMException('请求已取消', 'AbortError')
        if (!(error instanceof ApiError) || (error.status !== 401 && error.status !== 403)) throw error
        dispatchAuthRequired()
      }
    }
    if (!response.ok) {
      if (response.status === 401 && !retrySession && !path.startsWith('/auth/')) dispatchAuthRequired()
      throw new ApiError(body?.message ?? `请求失败（${response.status}）`, response.status, body?.code ?? 'HTTP_ERROR', traceId, body?.details)
    }
    if (response.status === 204) return { data: undefined as T, traceId }
    if (!body || !('data' in body)) throw new ApiError('服务端响应格式不正确', response.status, 'INVALID_RESPONSE', traceId)
    return { data: body.data, traceId }
  }

  get<T>(path: string, signal?: AbortSignal) { return this.request<T>(path, { signal }) }
  post<T>(path: string, input?: unknown, signal?: AbortSignal) { return this.request<T>(path, { signal, method: 'POST', body: JSON.stringify(input ?? {}) }) }
  put<T>(path: string, input: unknown) { return this.request<T>(path, { method: 'PUT', body: JSON.stringify(input) }) }
  delete(path: string) { return this.request<void>(path, { method: 'DELETE' }) }
  postWithMeta<T>(path: string, input?: unknown) { return this.requestWithMeta<T>(path, { method: 'POST', body: JSON.stringify(input ?? {}) }) }
  putWithMeta<T>(path: string, input: unknown) { return this.requestWithMeta<T>(path, { method: 'PUT', body: JSON.stringify(input) }) }
}

export const httpClient = new HttpClient()
